// src/components/AdminPanel/AdminVenues.js
import React, { useState, useEffect, useCallback } from "react";
import { adminApi } from "../../services/adminApi";
import { useCascadingLocations } from "../../hooks/useCascadingLocations";
import "../../css/okgather1.webflow.css";

function AdminVenues({ adminToken, tabName }) {
  const [venues, setVenues] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Filters
  const [countryFilter, setCountryFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [areaFilter, setAreaFilter] = useState("");
  const [nameFilter, setNameFilter] = useState("");

  // Create/Edit form
  const [formMode, setFormMode] = useState("create");
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [venueFormData, setVenueFormData] = useState({
    country: "",
    city: "",
    area: "",
    name: "",
    address: "",
  });

  // Use our hook
  const {
    loading: locLoading,
    error: locError,
    getCountries,
    getCitiesForCountry,
    getAreasForCountryCity,
  } = useCascadingLocations(adminToken);

  // Derive arrays for the form's dropdowns
  const countriesList = getCountries();
  const citiesList = venueFormData.country
    ? getCitiesForCountry(venueFormData.country)
    : [];
  const areasList =
    venueFormData.country && venueFormData.city
      ? getAreasForCountryCity(venueFormData.country, venueFormData.city)
      : [];

  const fetchVenues = useCallback(async () => {
    if (!adminToken) return;
    try {
      setLoading(true);
      setError("");
      const data = await adminApi.getVenuesWithLocation(adminToken, {
        country: countryFilter,
        city: cityFilter,
        area: areaFilter,
      });
      setVenues(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [adminToken, countryFilter, cityFilter, areaFilter]);

  // Filter venues by name on client side
  const filteredVenues = venues.filter(v =>
    !nameFilter || v.name.toLowerCase().includes(nameFilter.toLowerCase())
  );

  // Helper to show success message temporarily
  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(""), 3000);
  };


  useEffect(() => {
    if (!adminToken) return;
    fetchVenues();
  }, [adminToken, fetchVenues]);


  function handleFilterSubmit(e) {
    e.preventDefault();
    fetchVenues();
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setVenueFormData((prev) => ({ ...prev, [name]: value }));

    // If user changed "country", reset city/area
    if (name === "country") {
      setVenueFormData((prev) => ({
        ...prev,
        country: value,
        city: "",
        area: "",
      }));
    }
    // If user changed "city", reset area
    if (name === "city") {
      setVenueFormData((prev) => ({
        ...prev,
        city: value,
        area: "",
      }));
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!adminToken) return;

    try {
      setLoading(true);
      setError("");
      if (formMode === "create") {
        await adminApi.createVenue(venueFormData, adminToken);
        showSuccess("Venue created successfully!");
      } else if (formMode === "edit" && selectedVenue) {
        await adminApi.updateVenue(selectedVenue.id, venueFormData, adminToken);
        showSuccess("Venue updated successfully!");
      }
      resetForm();
      fetchVenues();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setVenueFormData({
      country: "",
      city: "",
      area: "",
      name: "",
      address: "",
    });
    setSelectedVenue(null);
    setFormMode("create");
  }

  function startEdit(venue) {
    setSelectedVenue(venue);
    setFormMode("edit");
    if (venue.location) {
      setVenueFormData({
        country: venue.location.country || "",
        city: venue.location.city || "",
        area: venue.location.area || "",
        name: venue.name || "",
        address: venue.address || "",
      });
    } else {
      setVenueFormData({
        country: "",
        city: "",
        area: "",
        name: venue.name || "",
        address: venue.address || "",
      });
    }
  }

  function cancelEdit() {
    resetForm();
    setError("");
  }

  async function handleDelete(venueId) {
    if (!adminToken) return;
    if (!window.confirm("Are you sure you want to delete this venue?")) return;

    try {
      setLoading(true);
      setError("");
      await adminApi.deleteVenue(venueId, adminToken);
      showSuccess("Venue deleted successfully!");
      fetchVenues();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function clearFilters() {
    setCountryFilter("");
    setCityFilter("");
    setAreaFilter("");
    setNameFilter("");
  }

  return (
    <div>
      <h3 style={{ textAlign: "center" }}>{tabName}</h3>

      <div style={{
        backgroundColor: '#f3f4f6',
        border: '1px solid #9ca3af',
        borderRadius: '8px',
        padding: '0.75rem 1rem',
        marginBottom: '1.25rem',
        fontSize: '0.9rem',
        color: '#374151',
      }}>
        🚫 <strong>No longer in use - managed on Meetup.</strong> Venue booking now happens through each
        theme's linked Meetup group, not OkGather. Kept here for reference only - see git history to reinstate
        if needed again.
      </div>

      {/* Success Message */}
      {successMessage && (
        <div style={{
          padding: "0.75rem 1rem",
          marginBottom: "1rem",
          backgroundColor: "#d4edda",
          color: "#155724",
          border: "1px solid #c3e6cb",
          borderRadius: "4px",
          textAlign: "center"
        }}>
          {successMessage}
        </div>
      )}

      {/* Error Message */}
      {(error || locError) && (
        <div style={{
          padding: "0.75rem 1rem",
          marginBottom: "1rem",
          backgroundColor: "#f8d7da",
          color: "#721c24",
          border: "1px solid #f5c6cb",
          borderRadius: "4px",
          textAlign: "center"
        }}>
          {error || locError}
        </div>
      )}

      {/* Loading Indicator */}
      {(loading || locLoading) && (
        <div style={{
          padding: "0.5rem",
          marginBottom: "1rem",
          backgroundColor: "#d1ecf1",
          color: "#0c5460",
          border: "1px solid #bee5eb",
          borderRadius: "4px",
          textAlign: "center"
        }}>
          Loading...
        </div>
      )}

      {/* Filters Section */}
      <div style={{
        backgroundColor: "#f8f9fa",
        padding: "1rem",
        borderRadius: "8px",
        marginBottom: "1.5rem"
      }}>
        <h4 style={{ marginTop: 0, marginBottom: "0.75rem" }}>Filters</h4>
        <form onSubmit={handleFilterSubmit} style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", alignItems: "flex-end" }}>
          <div style={{ flex: "1 1 150px" }}>
            <label style={{ display: "block", fontSize: "0.875rem", marginBottom: "0.25rem" }}>Country</label>
            <input
              type="text"
              placeholder="Filter by country"
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
              style={{ width: "100%", padding: "0.5rem", borderRadius: "4px", border: "1px solid #ccc" }}
            />
          </div>
          <div style={{ flex: "1 1 150px" }}>
            <label style={{ display: "block", fontSize: "0.875rem", marginBottom: "0.25rem" }}>City</label>
            <input
              type="text"
              placeholder="Filter by city"
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              style={{ width: "100%", padding: "0.5rem", borderRadius: "4px", border: "1px solid #ccc" }}
            />
          </div>
          <div style={{ flex: "1 1 150px" }}>
            <label style={{ display: "block", fontSize: "0.875rem", marginBottom: "0.25rem" }}>Area</label>
            <input
              type="text"
              placeholder="Filter by area"
              value={areaFilter}
              onChange={(e) => setAreaFilter(e.target.value)}
              style={{ width: "100%", padding: "0.5rem", borderRadius: "4px", border: "1px solid #ccc" }}
            />
          </div>
          <div style={{ flex: "1 1 150px" }}>
            <label style={{ display: "block", fontSize: "0.875rem", marginBottom: "0.25rem" }}>Venue Name</label>
            <input
              type="text"
              placeholder="Search by name"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              style={{ width: "100%", padding: "0.5rem", borderRadius: "4px", border: "1px solid #ccc" }}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            style={{
              padding: "0.5rem 1rem",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.6 : 1
            }}
          >
            Apply
          </button>
          <button
            type="button"
            onClick={clearFilters}
            disabled={loading}
            style={{
              padding: "0.5rem 1rem",
              backgroundColor: "#6c757d",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.6 : 1
            }}
          >
            Clear
          </button>
          <button
            type="button"
            onClick={fetchVenues}
            disabled={loading}
            style={{
              padding: "0.5rem 1rem",
              backgroundColor: "#28a745",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.6 : 1
            }}
          >
            Refresh
          </button>
        </form>
      </div>

      <div style={{ display: "flex", gap: "2rem", marginTop: "1rem", flexWrap: "wrap" }}>
        {/* Venues List */}
        <div style={{ flex: "1 1 500px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
            <h4 style={{ margin: 0 }}>Venues</h4>
            <span style={{ fontSize: "0.875rem", color: "#666" }}>
              Showing {filteredVenues.length} of {venues.length} venues
            </span>
          </div>
          {filteredVenues.length === 0 ? (
            <div style={{
              padding: "2rem",
              textAlign: "center",
              backgroundColor: "#f8f9fa",
              borderRadius: "8px",
              color: "#666"
            }}>
              No venues found
            </div>
          ) : (
            filteredVenues.map((v) => {
              const isEditing = selectedVenue && selectedVenue.id === v.id;
              return (
                <div
                  key={v.id}
                  style={{
                    border: isEditing ? "2px solid #007bff" : "1px solid #dee2e6",
                    margin: "0 0 0.75rem 0",
                    padding: "1rem",
                    borderRadius: "8px",
                    backgroundColor: isEditing ? "#e7f3ff" : "white",
                    transition: "all 0.2s ease"
                  }}
                >
                  {isEditing && (
                    <div style={{
                      fontSize: "0.75rem",
                      color: "#007bff",
                      fontWeight: "bold",
                      marginBottom: "0.5rem"
                    }}>
                      CURRENTLY EDITING
                    </div>
                  )}
                  <div style={{ marginBottom: "0.5rem" }}>
                    <strong style={{ color: "#495057" }}>ID:</strong>{" "}
                    <span style={{ color: "#6c757d" }}>{v.id}</span>
                  </div>
                  {v.location ? (
                    <div style={{ marginBottom: "0.5rem" }}>
                      <strong style={{ color: "#495057" }}>Location:</strong>{" "}
                      <span>{v.location.country}, {v.location.city}, {v.location.area}</span>
                    </div>
                  ) : (
                    <div style={{ marginBottom: "0.5rem", color: "#dc3545" }}>
                      No location found
                    </div>
                  )}
                  <div style={{ marginBottom: "0.5rem" }}>
                    <strong style={{ color: "#495057" }}>Name:</strong>{" "}
                    <span style={{ fontSize: "1.05rem" }}>{v.name}</span>
                  </div>
                  <div style={{ marginBottom: "0.75rem" }}>
                    <strong style={{ color: "#495057" }}>Address:</strong>{" "}
                    <span style={{ whiteSpace: "pre-wrap" }}>{v.address || "N/A"}</span>
                  </div>
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button
                      onClick={() => startEdit(v)}
                      disabled={loading}
                      style={{
                        padding: "0.4rem 0.9rem",
                        backgroundColor: isEditing ? "#6c757d" : "#007bff",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: loading ? "not-allowed" : "pointer",
                        fontSize: "0.875rem",
                        opacity: loading ? 0.6 : 1
                      }}
                    >
                      {isEditing ? "Editing..." : "Edit"}
                    </button>
                    <button
                      onClick={() => handleDelete(v.id)}
                      disabled={loading}
                      style={{
                        padding: "0.4rem 0.9rem",
                        backgroundColor: "#dc3545",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: loading ? "not-allowed" : "pointer",
                        fontSize: "0.875rem",
                        opacity: loading ? 0.6 : 1
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Create/Edit Form */}
        <div style={{ flex: "1 1 400px" }}>
          <div style={{
            backgroundColor: formMode === "edit" ? "#e7f3ff" : "#f8f9fa",
            padding: "1.5rem",
            borderRadius: "8px",
            border: formMode === "edit" ? "2px solid #007bff" : "1px solid #dee2e6"
          }}>
            <h4 style={{ marginTop: 0, marginBottom: "1rem" }}>
              {formMode === "create" ? "Create New Venue" : "Edit Venue"}
            </h4>
            <form onSubmit={handleSubmit} style={{ display: "grid", gap: "1rem" }}>
              <div>
                <label style={{
                  display: "block",
                  fontWeight: "500",
                  marginBottom: "0.25rem",
                  fontSize: "0.9rem"
                }}>
                  Country: <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <select
                  name="country"
                  value={venueFormData.country}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    borderRadius: "4px",
                    border: "1px solid #ced4da",
                    fontSize: "0.9rem"
                  }}
                >
                  <option value="">-- Select Country --</option>
                  {countriesList.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{
                  display: "block",
                  fontWeight: "500",
                  marginBottom: "0.25rem",
                  fontSize: "0.9rem"
                }}>
                  City: <span style={{ color: "#dc3545" }}>*</span>
                  <span style={{ fontWeight: "700", color: "#6b7280", fontSize: "0.85rem", marginLeft: "0.4rem" }}>
                    (go to Locations tab to edit/add city to list)
                  </span>
                </label>
                <select
                  name="city"
                  value={venueFormData.city}
                  onChange={handleChange}
                  disabled={!venueFormData.country}
                  required
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    borderRadius: "4px",
                    border: "1px solid #ced4da",
                    fontSize: "0.9rem",
                    opacity: !venueFormData.country ? 0.6 : 1,
                    cursor: !venueFormData.country ? "not-allowed" : "pointer"
                  }}
                >
                  <option value="">-- Select City --</option>
                  {citiesList.map((ct) => (
                    <option key={ct} value={ct}>
                      {ct}
                    </option>
                  ))}
                </select>
                {!venueFormData.country && (
                  <div style={{ fontSize: "0.75rem", color: "#6c757d", marginTop: "0.25rem" }}>
                    Please select a country first
                  </div>
                )}
              </div>
              <div>
                <label style={{
                  display: "block",
                  fontWeight: "500",
                  marginBottom: "0.25rem",
                  fontSize: "0.9rem"
                }}>
                  Area: <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <select
                  name="area"
                  value={venueFormData.area}
                  onChange={handleChange}
                  disabled={!venueFormData.city}
                  required
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    borderRadius: "4px",
                    border: "1px solid #ced4da",
                    fontSize: "0.9rem",
                    opacity: !venueFormData.city ? 0.6 : 1,
                    cursor: !venueFormData.city ? "not-allowed" : "pointer"
                  }}
                >
                  <option value="">-- Select Area --</option>
                  {areasList.map((ar) => (
                    <option key={ar} value={ar}>
                      {ar}
                    </option>
                  ))}
                </select>
                {!venueFormData.city && (
                  <div style={{ fontSize: "0.75rem", color: "#6c757d", marginTop: "0.25rem" }}>
                    Please select a city first
                  </div>
                )}
              </div>

              <div>
                <label style={{
                  display: "block",
                  fontWeight: "500",
                  marginBottom: "0.25rem",
                  fontSize: "0.9rem"
                }}>
                  Name: <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={venueFormData.name}
                  onChange={handleChange}
                  required
                  placeholder="Enter venue name"
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    borderRadius: "4px",
                    border: "1px solid #ced4da",
                    fontSize: "0.9rem",
                    boxSizing: "border-box"
                  }}
                />
              </div>
              <div>
                <label style={{
                  display: "block",
                  fontWeight: "500",
                  marginBottom: "0.25rem",
                  fontSize: "0.9rem"
                }}>
                  Address:
                </label>
                <textarea
                  name="address"
                  value={venueFormData.address}
                  onChange={handleChange}
                  placeholder="Enter venue address (optional)"
                  rows={2}
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    borderRadius: "4px",
                    border: "1px solid #ced4da",
                    fontSize: "0.9rem",
                    boxSizing: "border-box",
                    fontFamily: "inherit",
                    resize: "vertical"
                  }}
                />
              </div>

              <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    flex: 1,
                    padding: "0.65rem 1rem",
                    backgroundColor: formMode === "create" ? "#28a745" : "#007bff",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: loading ? "not-allowed" : "pointer",
                    fontWeight: "500",
                    fontSize: "0.9rem",
                    opacity: loading ? 0.6 : 1
                  }}
                >
                  {formMode === "create" ? "Create Venue" : "Update Venue"}
                </button>
                {formMode === "edit" && (
                  <button
                    type="button"
                    onClick={cancelEdit}
                    disabled={loading}
                    style={{
                      flex: 1,
                      padding: "0.65rem 1rem",
                      backgroundColor: "#6c757d",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: loading ? "not-allowed" : "pointer",
                      fontWeight: "500",
                      fontSize: "0.9rem",
                      opacity: loading ? 0.6 : 1
                    }}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminVenues;
